/**
 * Query key helpers for Shopify data fetching.
 *
 * The original enhanced query client paired these helpers with a customized
 * `QueryClient` implementation. The client logic has since moved into the
 * store-specific hooks, leaving the normalization helpers as the only portion
 * still referenced by the runtime. This module now focuses on those helpers
 * exclusively.
 */
import requestDeduplicationService from '../services/requestDeduplicationService';

export const queryKeyUtils = {
  /**
   * Create normalized product query key
   */
  products: (options: {
    first?: number;
    after?: string | null;
    sortKey?: string;
    reverse?: boolean;
    query?: string | null;
    optimized?: boolean;
  } = {}) => {
    return requestDeduplicationService.normalizeQueryKey([
      'shopify-products',
      options.first || 20,
      options.after || null,
      options.sortKey || 'TITLE',
      options.reverse || false,
      options.query || null,
      options.optimized !== false ? 'optimized' : 'legacy',
    ]);
  },

  /**
   * Create normalized collections query key
   */
  collections: (options: {
    first?: number;
    after?: string | null;
    sortKey?: string;
    reverse?: boolean;
    optimized?: boolean;
  } = {}) => {
    return requestDeduplicationService.normalizeQueryKey([
      'shopify-collections',
      options.first || 20,
      options.after || null,
      options.sortKey || 'TITLE',
      options.reverse || false,
      options.optimized !== false ? 'optimized' : 'legacy',
    ]);
  },

  /**
   * Create normalized infinite collections query key
   */
  infiniteCollections: (options: {
    first?: number;
    sortKey?: string;
    reverse?: boolean;
    optimized?: boolean;
  } = {}) => {
    return requestDeduplicationService.normalizeQueryKey([
      'shopify-collections-infinite',
      options.first || 20,
      options.sortKey || 'TITLE',
      options.reverse || false,
      options.optimized !== false ? 'optimized' : 'legacy',
    ]);
  },

  /**
   * Create normalized infinite query key
   */
  infiniteProducts: (options: {
    first?: number;
    sortKey?: string;
    reverse?: boolean;
    query?: string | null;
    optimized?: boolean;
  } = {}) => {
    return requestDeduplicationService.normalizeQueryKey([
      'shopify-products-infinite',
      options.first || 20,
      options.sortKey || 'TITLE',
      options.reverse || false,
      options.query || null,
      options.optimized !== false ? 'optimized' : 'legacy',
    ]);
  },

  /**
   * Create product detail query key
   */
  productDetail: (handle: string) => {
    return ['shopify-product-detail', handle];
  },

  /**
   * Create cart query key
   */
  cart: (cartId?: string) => {
    return cartId ? ['shopify-cart', cartId] : ['shopify-cart'];
  },
};
